import { motion } from 'framer-motion';
import { ArrowLeft } from 'lucide-react';

const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: 'easeOut' } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1, delayChildren: 0.1 }
  }
};

export default function PrivacyPolicyPage() {
  return (
    <div className="min-h-screen bg-deep-charcoal">
      {/* Navbar */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass-card border-b border-white/5 rounded-none">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <a href="/" className="flex items-center gap-2">
            <img src="/fans-audio-logo.png" alt="fans.audio logo" className="w-8 h-8 object-contain" />
            <span className="font-mono font-bold text-lg tracking-tight">fans<span className="text-electric-cyan">.audio</span></span>
          </a>
          <a href="/" className="flex items-center gap-2 text-sm text-text-secondary hover:text-electric-cyan transition-colors">
            <ArrowLeft className="w-4 h-4" />
            Back
          </a>
        </div>
      </nav>

      {/* Content */}
      <div className="pt-24 pb-20 px-4 sm:px-6 lg:px-8">
        <motion.div
          className="max-w-3xl mx-auto"
          initial="hidden"
          animate="visible"
          variants={staggerContainer}
        >
          {/* Header */}
          <motion.div variants={fadeInUp} className="mb-12">
            <span className="terminal-label text-xs">legal::privacy-policy</span>
            <h1 className="text-4xl sm:text-5xl font-bold mt-4 mb-4">
              Privacy <span className="text-electric-cyan">Policy</span>
            </h1>
            <p className="text-text-muted text-sm">Last Updated: May 2026</p>
          </motion.div>

          {/* Section 1 */}
          <motion.section variants={fadeInUp} className="mb-10">
            <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <span className="text-electric-cyan font-mono text-sm">01.</span> Introduction
            </h2>
            <div className="glass-card p-6 rounded-xl border border-white/5">
              <p className="text-text-secondary leading-relaxed">
                Welcome to fans.audio. We respect your privacy and are committed to protecting your personal data. This privacy policy informs you how we look after your personal data when you visit our website, use our services, or interact with us as an artist or fan.
              </p>
              <p className="text-text-secondary leading-relaxed mt-4">
                fans.audio is a trading style of Boogie Microphone Limited.
              </p>
            </div>
          </motion.section>

          {/* Section 2 */}
          <motion.section variants={fadeInUp} className="mb-10">
            <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <span className="text-electric-cyan font-mono text-sm">02.</span> Important Information and Who We Are
            </h2>
            <div className="glass-card p-6 rounded-xl border border-white/5">
              <p className="text-text-secondary leading-relaxed mb-4">
                Boogie Microphone Limited is the controller and responsible for your personal data (collectively referred to as "the Company", "we", "us" or "our" in this policy).
              </p>
              <h3 className="text-sm font-mono text-electric-cyan uppercase tracking-wider mb-3">Contact Details</h3>
              <p className="text-text-secondary leading-relaxed mb-2">
                If you have any questions about this privacy policy or our privacy practices, please contact us:
              </p>
              <ul className="space-y-2 text-text-secondary text-sm">
                <li className="flex gap-2">
                  <span className="text-electric-cyan font-mono">›</span>
                  <span><strong className="text-white">Legal entity:</strong> Boogie Microphone Limited (trading as fans.audio)</span>
                </li>
                <li className="flex gap-2">
                  <span className="text-electric-cyan font-mono">›</span>
                  <span><strong className="text-white">Email:</strong> admin@bmic.music</span>
                </li>
                <li className="flex gap-2">
                  <span className="text-electric-cyan font-mono">›</span>
                  <span><strong className="text-white">Postal address:</strong> 4 Yorke Street, Hucknall, Nottingham, England, NG15 7BT</span>
                </li>
                <li className="flex gap-2">
                  <span className="text-electric-cyan font-mono">›</span>
                  <span><strong className="text-white">Company Registration Number:</strong> 16815616</span>
                </li>
              </ul>
              <p className="text-text-secondary leading-relaxed mt-4 text-sm">
                You have the right to make a complaint at any time to the Information Commissioner's Office (ICO), the UK regulator for data protection issues (<a href="https://www.ico.org.uk" target="_blank" rel="noopener noreferrer" className="text-electric-cyan hover:underline">www.ico.org.uk</a>). We would, however, appreciate the chance to deal with your concerns before you approach the ICO, so please contact us in the first instance.
              </p>
            </div>
          </motion.section>

          {/* Section 3 */}
          <motion.section variants={fadeInUp} className="mb-10">
            <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <span className="text-electric-cyan font-mono text-sm">03.</span> The Data We Collect About You
            </h2>
            <div className="glass-card p-6 rounded-xl border border-white/5">
              <p className="text-text-secondary leading-relaxed mb-4">
                We may collect, use, store and transfer different kinds of personal data about you which we have grouped together as follows:
              </p>
              <ul className="space-y-3 text-text-secondary text-sm">
                <li className="flex gap-2">
                  <span className="text-electric-cyan font-mono">›</span>
                  <span><strong className="text-white">Identity Data:</strong> Name, username, or similar identifier.</span>
                </li>
                <li className="flex gap-2">
                  <span className="text-electric-cyan font-mono">›</span>
                  <span><strong className="text-white">Contact Data:</strong> Billing address, email address, and telephone numbers.</span>
                </li>
                <li className="flex gap-2">
                  <span className="text-electric-cyan font-mono">›</span>
                  <span><strong className="text-white">Financial Data:</strong> (For Artists/Labels) Bank account and payment details for royalty distributions.</span>
                </li>
                <li className="flex gap-2">
                  <span className="text-electric-cyan font-mono">›</span>
                  <span><strong className="text-white">Technical Data:</strong> IP address, login data, browser type/version, and location data.</span>
                </li>
                <li className="flex gap-2">
                  <span className="text-electric-cyan font-mono">›</span>
                  <span><strong className="text-white">Usage Data:</strong> Information about how you use our website and audio services.</span>
                </li>
                <li className="flex gap-2">
                  <span className="text-electric-cyan font-mono">›</span>
                  <span><strong className="text-white">Marketing and Communications Data:</strong> Your preferences in receiving marketing from us.</span>
                </li>
              </ul>
            </div>
          </motion.section>

          {/* Section 4 */}
          <motion.section variants={fadeInUp} className="mb-10">
            <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <span className="text-electric-cyan font-mono text-sm">04.</span> How We Use Your Personal Data
            </h2>
            <div className="glass-card p-6 rounded-xl border border-white/5">
              <p className="text-text-secondary leading-relaxed mb-4">
                We will only use your personal data when the law allows us to. Most commonly, we will use your personal data in the following circumstances:
              </p>
              <ul className="space-y-3 text-text-secondary text-sm">
                <li className="flex gap-2">
                  <span className="text-electric-cyan font-mono">›</span>
                  <span><strong className="text-white">Performance of a Contract:</strong> Where we need to perform the contract we are about to enter into or have entered into with you (e.g., distributing music or providing access to the fans.audio platform).</span>
                </li>
                <li className="flex gap-2">
                  <span className="text-electric-cyan font-mono">›</span>
                  <span><strong className="text-white">Legitimate Interests:</strong> Where it is necessary for our legitimate interests (or those of a third party) and your interests and fundamental rights do not override those interests (e.g., for fraud prevention or platform analytics).</span>
                </li>
                <li className="flex gap-2">
                  <span className="text-electric-cyan font-mono">›</span>
                  <span><strong className="text-white">Comply with a Legal Obligation:</strong> Where we need to comply with a legal or regulatory obligation (e.g., tax reporting).</span>
                </li>
              </ul>
            </div>
          </motion.section>

          {/* Section 5 */}
          <motion.section variants={fadeInUp} className="mb-10">
            <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <span className="text-electric-cyan font-mono text-sm">05.</span> Data Security
            </h2>
            <div className="glass-card p-6 rounded-xl border border-white/5">
              <p className="text-text-secondary leading-relaxed">
                We have put in place appropriate security measures to prevent your personal data from being accidentally lost, used, or accessed in an unauthorised way. We limit access to your personal data to those employees, agents, and contractors who have a business need to know.
              </p>
            </div>
          </motion.section>

          {/* Section 6 */}
          <motion.section variants={fadeInUp} className="mb-10">
            <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <span className="text-electric-cyan font-mono text-sm">06.</span> Data Retention
            </h2>
            <div className="glass-card p-6 rounded-xl border border-white/5">
              <p className="text-text-secondary leading-relaxed">
                We will only retain your personal data for as long as reasonably necessary to fulfil the purposes we collected it for, including for the purposes of satisfying any legal, regulatory, tax, accounting or reporting requirements.
              </p>
            </div>
          </motion.section>

          {/* Section 7 */}
          <motion.section variants={fadeInUp} className="mb-10">
            <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <span className="text-electric-cyan font-mono text-sm">07.</span> Your Legal Rights
            </h2>
            <div className="glass-card p-6 rounded-xl border border-white/5">
              <p className="text-text-secondary leading-relaxed mb-4">
                Under certain circumstances, you have rights under data protection laws in relation to your personal data, including:
              </p>
              <ul className="space-y-2 text-text-secondary text-sm">
                <li className="flex gap-2">
                  <span className="text-electric-cyan font-mono">›</span>
                  <span>Request access to your personal data.</span>
                </li>
                <li className="flex gap-2">
                  <span className="text-electric-cyan font-mono">›</span>
                  <span>Request correction of your personal data.</span>
                </li>
                <li className="flex gap-2">
                  <span className="text-electric-cyan font-mono">›</span>
                  <span>Request erasure of your personal data.</span>
                </li>
                <li className="flex gap-2">
                  <span className="text-electric-cyan font-mono">›</span>
                  <span>Object to processing of your personal data.</span>
                </li>
                <li className="flex gap-2">
                  <span className="text-electric-cyan font-mono">›</span>
                  <span>Request restriction of processing your personal data.</span>
                </li>
                <li className="flex gap-2">
                  <span className="text-electric-cyan font-mono">›</span>
                  <span>Request transfer of your personal data.</span>
                </li>
                <li className="flex gap-2">
                  <span className="text-electric-cyan font-mono">›</span>
                  <span>Right to withdraw consent.</span>
                </li>
                <li className="flex gap-2">
                  <span className="text-electric-cyan font-mono">›</span>
                  <span><strong className="text-white">Right to Complain:</strong> In accordance with the Data (Use and Access) Act 2025, you have a formal right to complain to us. We will acknowledge receipt of your complaint within 30 days and investigate without undue delay.</span>
                </li>
              </ul>
            </div>
          </motion.section>

          {/* Section 8 */}
          <motion.section variants={fadeInUp} className="mb-10">
            <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <span className="text-electric-cyan font-mono text-sm">08.</span> Third-Party Links
            </h2>
            <div className="glass-card p-6 rounded-xl border border-white/5">
              <p className="text-text-secondary leading-relaxed">
                This website may include links to third-party websites (e.g., streaming services or social media). Clicking on those links or enabling those connections may allow third parties to collect or share data about you. We do not control these third-party websites and are not responsible for their privacy statements.
              </p>
            </div>
          </motion.section>

          {/* Footer note */}
          <motion.div variants={fadeInUp} className="mt-12 pt-8 border-t border-white/5 text-center">
            <p className="text-text-muted text-xs font-mono">
              &gt; fans.audio — a trading style of Boogie Microphone Limited
            </p>
          </motion.div>
        </motion.div>
      </div>
    </div>
  );
}
